import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEye, faSpinner, faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import BasketSidebar from '../../common/BasketSidebar';
import { woocommerceFarmService } from '../../../services/woocommerceFarmService';
import { useBasket } from '../../../contexts/BasketContext';

const farmshopLogo = 'https://BelvoirCastle.b-cdn.net/Logos/farmshopLogo.webp';

const farmshopBanner1 = 'https://BelvoirCastle.b-cdn.net/Banners/farmshop-1.webp';
const farmshopBanner2 = 'https://BelvoirCastle.b-cdn.net/Banners/farmshop-2.webp';
const farmshopBanner3 = 'https://BelvoirCastle.b-cdn.net/Banners/farmshop-4.webp';

// Helper function to clean up HTML entities (e.g., &amp; to &)
const cleanText = (text) => {
  if (!text) return '';
  return text.replace(/&amp;/g, '&');
};

const ShopContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.offWhite};
  padding-top: 200px;
  
  @media (max-width: 768px) {
    padding-top: 160px;
  }
  
  /* Prevent horizontal scroll on small viewports */
  overflow-x: hidden;
  max-width: 100vw;
  box-sizing: border-box;
`;

// Fullscreen Loader (Duchess style with Farm Shop logo)
const FullscreenLoader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(1200px 600px at 50% -20%, rgba(157, 109, 73, 0.08), transparent),
    linear-gradient(180deg, #ffffff 0%, #faf7f3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  z-index: 2000;
  gap: 1rem;
`;

const LoaderWrapper = styled.div`
  position: relative;
  width: 180px;
  height: 180px;
  display: grid;
  place-items: center;
`;

const LoaderRing = styled.div`
  position: absolute;
  width: ${props => props.size || 180}px;
  height: ${props => props.size || 180}px;
  border-radius: 50%;
  border: 3px solid rgba(16, 29, 64, 0.08);
  border-top-color: ${props => props.color || props.theme.colors.accent};
  animation: spin ${props => props.duration || 1.4}s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoaderLogo = styled.img`
  width: 120px;
  height: auto;
  filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.18));
  animation: float 3s ease-in-out infinite;

  @keyframes float {
    0% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
    100% { transform: translateY(0); }
  }
`;

const ProgressContainer = styled.div`
  width: 220px;
  height: 6px;
  background: rgba(16, 29, 64, 0.08);
  border-radius: 999px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06) inset;
`;

const ProgressShimmer = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(157, 109, 73, 0.35), transparent);
  background-size: 200% 100%;
  animation: progressShift 1.3s linear infinite;

  @keyframes progressShift {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
  position: relative;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 0.875rem;
  }
`;

const ContentLoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
`;

// Slider Section
const SliderSection = styled.section`
  position: relative;
  height: 500px;
  width: 100%;
  margin: 0 auto 3rem;
  padding-top: 160px;
  overflow: hidden;
  max-width: 1400px;
  background: #101d40;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  isolation: isolate;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    margin-left: 1.5rem;
    margin-right: 1.5rem;
    margin-top: 40px;
    margin-bottom: 2.5rem;
  }

  @media (max-width: 1024px) {
    height: 450px;
    margin-left: 1.5rem;
    margin-right: 1.5rem;
    margin-top: 20px;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 768px) {
    height: 400px;
    margin-left: 0;
    margin-right: 0;
    margin-top: 10px;
    margin-bottom: 2rem;
    padding-top: 120px;
  }

  @media (max-width: 480px) {
    margin-left: 0;
    margin-right: 0;
    margin-top: 0.5rem;
    margin-bottom: 2rem;
  }
`;

const Slide = styled.div`
  position: absolute;
  inset: 0;
  background: ${props => `url('${props.$image}') center/cover no-repeat`};
  transform: translateX(${props => (props.$active ? '0%' : props.$direction)});
  transition: transform 0.7s ease-in-out;
  will-change: transform;

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(5, 10, 25, 0.45);
    mix-blend-mode: multiply;
    z-index: 1;
  }

  &:after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      rgba(16, 29, 64, 0.8) 0%,
      rgba(16, 29, 64, 0.6) 50%,
      rgba(16, 29, 64, 0.8) 100%
    );
    z-index: 2;
  }
`;

const SlideContent = styled.div`
  position: relative;
  z-index: 3;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  color: white;
  padding: 0 clamp(1rem, 5vw, 5rem);
  pointer-events: none;
`;

const HeroTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(2rem, 4vw, 3rem);
  margin: 0 0 0.75rem 0;
  color: ${props => props.theme.colors.primary};
  line-height: 1.2;
  max-width: 600px;
  font-weight: 600;
`;

const HeroSubtitle = styled.p`
  font-size: clamp(1rem, 1.8vw, 1.2rem);
  margin: 0;
  line-height: 1.6;
  color: #4a5568;
  max-width: 560px;
`;

const HeroPanel = styled.div`
  background: rgba(255, 255, 255, 0.95);
  padding: clamp(1.5rem, 3vw, 2.5rem);
  border: 2px solid #e0e0e0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  pointer-events: auto;

  @media (max-width: 768px) {
    padding: 1.25rem;
    gap: 0.75rem;
  }
`;

const HeroEyebrow = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  display: block;
  margin-bottom: 0.5rem;
`;

const HeroDescription = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #5a6c7d;
`;

const HeroCtaHint = styled.span`
  display: inline-block;
  margin-top: 0.75rem;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  color: #718096;
`;

const WineTrailLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.5rem 0;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.accent};
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    border-bottom-color: ${props => props.theme.colors.primary};
  }
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: translateX(3px);
  }
`;

const HeroCta = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: 2px solid ${props => props.theme.colors.primary};
  padding: 0.875rem 2rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin-top: 0.5rem;
  
  &:hover {
    background: white;
    color: ${props => props.theme.colors.primary};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const SliderControls = styled.div`
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 3;
`;

const Dot = styled.button`
  width: 10px;
  height: 10px;
  border: 1px solid white;
  background: ${props => props.$active ? 'white' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${props => (props.$active ? 1 : 0.7)};
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.$left ? 'left: 1rem;' : 'right: 1rem;'}
  z-index: 3;
  background: white;
  color: ${props => props.theme.colors.primary};
  border: 1px solid rgba(255, 255, 255, 0.8);
  width: 42px;
  height: 42px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.25);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.25rem;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  margin-bottom: 2.5rem;
  position: relative;
  font-weight: 600;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 2px;
    background: ${props => props.theme.colors.accent};
  }
`;

const SearchAndFilters = styled.div`
  background: white;
  border: 1px solid #d0d0d0;
  padding: 1.5rem;
  margin-bottom: 2.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 2rem;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    gap: 1rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  min-width: 0;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    max-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1.25rem 0.75rem 3rem;
  border: 1px solid #c0c0c0;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #fafafa;
  
  @media (max-width: 360px) {
    padding: 0.7rem 1rem 0.7rem 2.8rem;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(16, 29, 64, 0.1);
    background: white;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1.2rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.accent};
  font-size: 1.1rem;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  & > * {
    flex: 0 0 auto;
  }
  
  @media (max-width: 1024px) {
    justify-content: center;
  }
  
  @media (max-width: 768px) {
    justify-content: space-between;
    gap: 0.8rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1.25rem;
  border: 1px solid #c0c0c0;
  background-color: #fafafa;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 160px;
  white-space: nowrap;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(16, 29, 64, 0.1);
    background-color: white;
  }
  
  @media (max-width: 768px) {
    min-width: 140px;
    padding: 0.7rem 1.1rem;
  }
  
  @media (max-width: 480px) {
    min-width: 100%;
  }
`;

const SortSelect = styled.select`
  padding: 0.75rem 1.25rem;
  border: 1px solid #c0c0c0;
  background-color: #fafafa;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 160px;
  white-space: nowrap;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(16, 29, 64, 0.1);
    background-color: white;
  }
  
  @media (max-width: 768px) {
    min-width: 140px;
    padding: 0.7rem 1.1rem;
  }
  
  @media (max-width: 480px) {
    min-width: 100%;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  /* Match Duchess breakpoints for consistent layout */
  @media (min-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 3rem;
  }
  
  @media (min-width: 1200px) and (max-width: 1399px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 2.5rem;
  }
  
  @media (min-width: 1000px) and (max-width: 1199px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2.5rem;
  }
  
  @media (min-width: 768px) and (max-width: 999px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
  
  @media (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.875rem;
  }
  
  @media (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: 0.875rem;
  }
`;

const ProductCard = styled.div`
  position: relative;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    border-color: #c0c0c0;
  }

  /* Disable hover-based scale on touch devices to prevent overflow */
  @media (hover: none) {
    &:hover {
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
      border-color: #e0e0e0;
    }
  }
`;

const ProductImageContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
`;

const ProductImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  image-rendering: auto;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  transition: opacity 0.3s ease;
  
  ${ProductCard}:hover & {
    opacity: 0.85;
  }

  @media (hover: none) {
    ${ProductCard}:hover & {
      opacity: 1;
    }
  }
`;

const ProductImageSecondary = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  image-rendering: auto;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 2;

  ${ProductCard}:hover & {
    opacity: 1;
  }

  @media (hover: none) {
    ${ProductCard}:hover & {
      opacity: 0;
    }
  }
`;

const ProductOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0.75rem;
  background: transparent;
`;

const ProductActions = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 15;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  ${ProductCard}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid #c0c0c0;
  background: white;
  color: #101d40;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    background: #101d40;
    color: white;
    border-color: #101d40;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  }
`;

const CardInfo = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1rem;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-weight: 600;
  line-height: 1.3;
  word-break: break-word;
  overflow-wrap: anywhere;
  hyphens: auto;
`;

const CardPrice = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
`;

const ProductBadge = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 15;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.35rem 0.75rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
`;

const getButtonBackground = (status) => {
  switch (status) {
    case 'added':
      return '#4CAF50';
    case 'out':
      return '#9e9e9e';
    case 'limit':
      return '#f59e0b';
    default:
      return '#101d40';
  }
};

const getButtonHoverBackground = (status) => {
  switch (status) {
    case 'added':
      return '#45a049';
    case 'out':
      return '#8f8f8f';
    case 'limit':
      return '#e08e0a';
    default:
      return '#0d1730';
  }
};

const AddToCartButton = styled.button`
  width: 100%;
  padding: 0.75rem 1.25rem;
  background: ${({ $status }) => getButtonBackground($status)};
  color: white;
  border: 1px solid ${({ $status }) => getButtonBackground($status)};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: ${({ disabled }) => (disabled ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)')};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0;
  transform: translateY(-5px);
  pointer-events: none;
  
  ${ProductCard}:hover & {
    opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
    transform: translateY(0);
    pointer-events: auto;
  }
  
  &:hover {
    ${({ disabled, $status }) =>
      disabled
        ? `
          box-shadow: none;
        `
        : `
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
          background: ${getButtonHoverBackground($status)};
          border-color: ${getButtonHoverBackground($status)};
        `}
  }
  
  @media (hover: none) {
    /* On touch devices, always show the button */
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
`;

const HERO_SLIDES = [
  {
    image: farmshopBanner1,
    eyebrow: 'Estate Boutique',
    title: 'Belvoir Hampers & Wines',
    subtitle: 'Hand-packed gifts with sparkling elderflower, reserve wines and pantry favourites.',
    description:
      'Build bespoke hampers filled with estate chutneys, truffles, preserves and limited Belvoir wines ready to gift.',
    ctaLabel: 'Shop hampers',
 
  },
  {
    image: farmshopBanner2,
    eyebrow: 'In-store Showcase',
    title: 'Seasonal gifting arrivals',
    subtitle: 'Hampers, wines and tableware styling greet you as you step inside our shop.',
    description:
      'Explore tiered hamper towers, Belvoir wines, preserves and curated homeware capsules throughout the shop, ready to take home or gift.',
    ctaLabel: 'View seasonal picks',
   
    
  },
  {
    image: farmshopBanner3,
    eyebrow: 'Butchery & Produce',
    title: 'Heritage cuts & seasonal veg',
    subtitle: 'Our butchers prepare estate beef, lamb and charcuterie beside baskets of fresh greens.',
    description:
      'Choose from dry-aged joints, handmade sausages and deli meats, then add garden vegetables, dairy and pantry staples for the full Belvoir larder.',
    ctaLabel: 'Browse the butcher',
  
  }
];

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6rem 2rem;
  flex-direction: column;
  gap: 1.5rem;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 4rem;
  color: #F44336;
`;

const RetryButton = styled.button`
  padding: 0.875rem 2rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: 1px solid ${props => props.theme.colors.primary};
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 4rem;
  flex-wrap: wrap;
  padding: 0 1rem;
  max-width: 100%;
  box-sizing: border-box;
`;

const PaginationButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid ${props => props.theme.colors.primary};
  background-color: ${props => props.active ? props.theme.colors.primary : 'white'};
  color: ${props => props.active ? 'white' : props.theme.colors.primary};
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
    box-shadow: 0 2px 6px rgba(16, 29, 64, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
  
  &:disabled:hover {
    background-color: ${props => props.active ? props.theme.colors.primary : 'white'};
    color: ${props => props.active ? 'white' : props.theme.colors.primary};
  }
`;

const FarmShopShop = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToBasket, getItemQuantity } = useBasket();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [minLoadingTime, setMinLoadingTime] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeSlide, setActiveSlide] = useState(0);
  const [showBasket, setShowBasket] = useState(false);
  const [addedToCart, setAddedToCart] = useState({});

  const productsPerPage = 48;

  const slides = HERO_SLIDES;
  const shopSectionRef = useRef(null);

  const scrollToShop = useCallback(() => {
    if (typeof window === 'undefined' || !shopSectionRef.current) return;
    const offsetTop = shopSectionRef.current.getBoundingClientRect().top + window.pageYOffset - 120;
    window.scrollTo({ top: Math.max(offsetTop, 0), behavior: 'smooth' });
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setMinLoadingTime(false);
      setError(null);
      const minTimePromise = new Promise(resolve => {
        setTimeout(() => { setMinLoadingTime(true); resolve(); }, 800);
      });
      const options = {
        page: currentPage,
        per_page: productsPerPage,
        search: searchTerm,
        category: selectedCategory,
        orderby: sortBy,
        order: 'desc'
      };
      const response = await woocommerceFarmService.getProducts(options);
      // Handle both old format (array) and new format (object with products, total, totalPages)
      const productsData = Array.isArray(response) ? response : (response?.products || []);
      const filteredProducts = Array.isArray(productsData)
        ? productsData.filter(product => product.stock_status === 'instock')
        : [];
      setProducts(filteredProducts);
      // Use pagination info from API if available, otherwise calculate from filtered products
      if (response && typeof response === 'object' && !Array.isArray(response) && response.totalPages) {
        setTotalPages(response.totalPages);
      } else {
        setTotalPages(Math.max(1, Math.ceil(filteredProducts.length / productsPerPage)));
      }
      await minTimePromise;
    } catch (err) {
      // Enhanced error handling with more helpful messages
      let errorMessage = 'We\'re currently updating our shop. Please check back soon.';
      
      if (err.message?.includes('401')) {
        errorMessage = 'Shop authentication issue. Our team has been notified.';
      } else if (err.message?.includes('502') || err.message?.includes('Bad Gateway')) {
        errorMessage = 'Shop temporarily unavailable. We\'re working to restore service.';
      } else if (err.message?.includes('Authentication failed')) {
        errorMessage = 'Shop configuration in progress. Please try again later.';
      }
      
      setError(errorMessage);
      await new Promise(resolve => setTimeout(resolve, 800));
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const categoriesData = await woocommerceFarmService.getCategories();
      setCategories(categoriesData);
    } catch (_) {}
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    const cat = params.get('category') || '';
    const srt = params.get('sort') || 'date';
    setSearchTerm(q);
    setSelectedCategory(cat);
    setSortBy(srt);
    fetchCategories();
    fetchProducts();
  }, [location.search]);

  // Listen for global event to open basket (from Navbar)
  useEffect(() => {
    const handleOpenBasket = () => setShowBasket(true);
    document.addEventListener('openBasketSidebar', handleOpenBasket);
    return () => document.removeEventListener('openBasketSidebar', handleOpenBasket);
  }, []);

  // Disabled auto-rotation since we're only showing the first slide
  // useEffect(() => {
  //   if (slides.length <= 1) return;
  //   const id = setInterval(() => setActiveSlide(prev => (prev + 1) % slides.length), 6000);
  //   return () => clearInterval(id);
  // }, [slides.length]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const params = new URLSearchParams(location.search);
      if (searchTerm) params.set('q', searchTerm); else params.delete('q');
      navigate({ pathname: '/farm-shop', search: params.toString() }, { replace: true });
      fetchProducts();
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setSelectedCategory(value);
    setCurrentPage(1);
    const params = new URLSearchParams(location.search);
    if (value) params.set('category', value); else params.delete('category');
    navigate({ pathname: '/farm-shop', search: params.toString() }, { replace: true });
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    setSortBy(value);
    setCurrentPage(1);
    const params = new URLSearchParams(location.search);
    if (value) params.set('sort', value); else params.delete('sort');
    navigate({ pathname: '/farm-shop', search: params.toString() }, { replace: true });
  };

  // Handle add to cart
  const handleAddToCart = (product) => {
    const result = addToBasket(product, 1);
    if (!result?.success) {
      const statusKey = result?.reason === 'limit_reached' ? 'limit' : 'out';
      setAddedToCart(prev => ({ ...prev, [product.id]: statusKey }));
      setTimeout(() => {
        setAddedToCart(prev => {
          const copy = { ...prev };
          delete copy[product.id];
          return copy;
        });
      }, 2500);
      return;
    }

    setAddedToCart(prev => ({ ...prev, [product.id]: 'added' }));
    setTimeout(() => {
      setAddedToCart(prev => {
        const copy = { ...prev };
        delete copy[product.id];
        return copy;
      });
    }, 2000);
  };

  // Handle quick view (placeholder)
  const handleQuickView = (product) => {

    alert(`Quick view for ${product.name}`);
  };

  // Handle product click - navigate to detail page
  const handleProductClick = (product) => {
    navigate(`/farm-shop/product/${product.id}`);
  };

  const renderPrice = (product) => {
    if (product.sale_price && product.sale_price !== product.regular_price) {
      return (
        <>
          £{product.sale_price}
          <span style={{ textDecoration: 'line-through', opacity: 0.7, marginLeft: '0.5rem' }}>£{product.regular_price}</span>
        </>
      );
    }
    return <>£{product.price}</>;
  };

  // Fullscreen loader when initial load
  if (loading && products.length === 0) {
    return (
      <>
        <FullscreenLoader>
          <LoaderWrapper>
            <LoaderRing size={180} duration={1.4} color={'#9d6d49'} />
            <LoaderRing size={140} duration={1.8} color={'#101d40'} />
            <LoaderRing size={100} duration={2.2} color={'#d4a574'} />
            <LoaderLogo src={farmshopLogo} alt="Belvoir Farm Shop" />
          </LoaderWrapper>
          <ProgressContainer>
            <ProgressShimmer />
          </ProgressContainer>
        </FullscreenLoader>
      </>
    );
  }

  if (error) {
    return (
      <ShopContainer>
        <ErrorContainer>
          <ErrorIcon>⚠️</ErrorIcon>
          <h2>Oops! Something went wrong</h2>
          <p>{error}</p>
          <RetryButton onClick={() => fetchProducts()}>Try Again</RetryButton>
        </ErrorContainer>
        <Footer />
      </ShopContainer>
    );
  }

  return (
    <ShopContainer>
      {/* Hero Section - Static first slide only */}
      <SliderSection>
        {/* Only show first slide - no rotation */}
        <Slide $image={slides[0].image} $active={true} $direction="0%">
          <SlideContent>
            <HeroPanel>
              {slides[0].eyebrow && <HeroEyebrow>{slides[0].eyebrow}</HeroEyebrow>}
              <HeroTitle>{slides[0].title}</HeroTitle>
              <HeroSubtitle>{slides[0].subtitle}</HeroSubtitle>
              {slides[0].description && <HeroDescription>{slides[0].description}</HeroDescription>}
              <div>
                <HeroCta type="button" onClick={scrollToShop} aria-label={slides[0].ctaLabel || 'Shop now'}>
                  {slides[0].ctaLabel || 'Shop now'}
                  <FontAwesomeIcon icon={faArrowRight} />
                </HeroCta>
                {slides[0].ctaHint && <HeroCtaHint>{slides[0].ctaHint}</HeroCtaHint>}
              </div>
              <WineTrailLink href="https://www.eastmidlandswinetrail.co.uk" target="_blank" rel="noopener noreferrer">
                Visit East Midlands Wine Trail
                <FontAwesomeIcon icon={faArrowRight} />
              </WineTrailLink>
            </HeroPanel>
          </SlideContent>
        </Slide>
      </SliderSection>

      <ContentContainer ref={shopSectionRef}>
        {loading && products.length > 0 && !minLoadingTime && (
          <ContentLoadingOverlay>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#9d6d49' }} />
          </ContentLoadingOverlay>
        )}
        <SectionTitle>Belvoir Farm Shop</SectionTitle>

        <SearchAndFilters>
          <SearchContainer>
            <SearchIcon>
              <FontAwesomeIcon icon={faSearch} />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search Farm Shop..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
          <FilterContainer>
            <FilterSelect value={selectedCategory} onChange={handleCategoryChange}>
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{cleanText(category.name)}</option>
              ))}
            </FilterSelect>
            <SortSelect value={sortBy} onChange={handleSortChange}>
              <option value="date">Newest First</option>
              <option value="popularity">Most Popular</option>
              <option value="rating">Highest Rated</option>
              <option value="price">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name">Name: A to Z</option>
            </SortSelect>
          </FilterContainer>
        </SearchAndFilters>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#9d6d49' }} />
          </div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem' }}>No products found.</div>
        ) : (
          <>
            <ProductsGrid>
              {products.map(product => {
                const status = addedToCart[product.id];
                const managedStock = Boolean(product.manage_stock);
                const stockQuantityParsed = managedStock ? parseInt(product.stock_quantity, 10) : null;
                const stockQuantity = managedStock && Number.isFinite(stockQuantityParsed) ? Math.max(stockQuantityParsed, 0) : null;
                const currentBasketQty = getItemQuantity(product.id);
                const userLimitReached = managedStock && stockQuantity !== null && currentBasketQty >= stockQuantity;
                const isOutOfStock = product.stock_status !== 'instock' || (managedStock && stockQuantity !== null && stockQuantity <= 0);
                const buttonDisabled = isOutOfStock || userLimitReached || status === 'out' || status === 'limit';
                const activeStatus = buttonDisabled
                  ? (isOutOfStock ? 'out' : (userLimitReached || status === 'limit') ? 'limit' : status || 'out')
                  : status;
                const buttonLabel = status === 'added'
                  ? 'Added'
                  : (status === 'limit' || userLimitReached)
                    ? 'Max in basket'
                    : (status === 'out' || isOutOfStock)
                      ? 'Sold out'
                      : 'Add to Basket';

                // Get the best quality image available
                // WooCommerce image URLs sometimes have size suffixes like -300x300.jpg
                // Remove these to get the full-size, uncompressed image
                const getBestImageUrl = (imageObj) => {
                  if (!imageObj) return '/placeholder-product.jpg';
                  let imageUrl = imageObj.src || '/placeholder-product.jpg';
                  // Remove size suffixes from WooCommerce image URLs (e.g., -300x300, -600x600)
                  // This ensures we get the full-size, uncompressed image
                  // Match patterns like: image-300x300.jpg, image-600x600.webp, etc.
                  imageUrl = imageUrl.replace(/-\d+x\d+(?=\.(jpg|jpeg|png|webp|gif))/i, '');
                  return imageUrl;
                };

                return (
                <ProductCard key={product.id} onClick={() => handleProductClick(product)}>
                  <ProductImageContainer>
                    <ProductImage
                      src={getBestImageUrl(product.images?.[0])}
                      alt={cleanText(product.name)}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => { e.target.src = '/placeholder-product.jpg'; }}
                    />
                    {product.images?.[1] && (
                      <ProductImageSecondary
                        src={getBestImageUrl(product.images?.[1])}
                        alt={`${cleanText(product.name)} alternate view`}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <ProductOverlay>
                      <ProductActions>
                        <ActionButton onClick={(e) => { 
                          e.stopPropagation(); 
                          handleQuickView(product);
                        }}>
                          <FontAwesomeIcon icon={faEye} />
                        </ActionButton>
                      </ProductActions>
                    </ProductOverlay>
                    {product.sale_price && product.sale_price !== product.regular_price && (
                      <ProductBadge>Sale</ProductBadge>
                    )}
                    {product.featured && (
                      <ProductBadge>Featured</ProductBadge>
                    )}
                  </ProductImageContainer>
                  <CardInfo>
                    <CardTitle>{cleanText(product.name)}</CardTitle>
                    <CardPrice>{renderPrice(product)}</CardPrice>
                    <AddToCartButton 
                      $status={activeStatus}
                      disabled={buttonDisabled}
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        if (!buttonDisabled) handleAddToCart(product); 
                      }}
                    >
                      {buttonLabel}
                    </AddToCartButton>
                  </CardInfo>
                </ProductCard>
              );})}
            </ProductsGrid>

            {totalPages > 1 && (
              <Pagination>
                <PaginationButton onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>
                  <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '0.5rem' }} /> Previous
                </PaginationButton>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <PaginationButton key={page} onClick={() => setCurrentPage(page)} active={page === currentPage}>
                    {page}
                  </PaginationButton>
                ))}
                <PaginationButton onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>
                  Next <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '0.5rem' }} />
                </PaginationButton>
              </Pagination>
            )}
          </>
        )}
      </ContentContainer>
      <Footer />
      <BasketSidebar isOpen={showBasket} onClose={() => setShowBasket(false)} />
    </ShopContainer>
  );
};

export default FarmShopShop;

